// Shared Work-section ATOMS (brief §5.2/§5.3): the tag chip, the needs-a-decision mini badge, the
// section head with a source note, the content-hash chip, and the evidence as-of pill. Built ON TOP of
// the existing primitives (Dot / StatusPill / SectionHead) — nothing here duplicates primitives.tsx.
// Tokens only (G-181); every rendered state derives from projection data (G-182) — a badge kind or
// staleness flag is an input, never invented here. Behavioural test hooks ride data attributes.

import type { ReactNode } from 'react';
import { Dot, SectionHead, StatusPill, type Tone } from '../primitives.tsx';

/** An item tag chip (`disputed`, `VIP` — brief §3.2 tags). Quiet, radius-sm, sunken. */
export function WorkTag({ label }: { readonly label: string }): React.JSX.Element {
  return (
    <span
      data-tag={label}
      className="inline-flex items-center rounded-sm border border-border bg-surface-sunken px-1.5 py-0.5 text-xs text-text-muted"
    >
      {label}
    </span>
  );
}

/** The mini-badge kinds a board card / decision block carries: `decide` (a verdict is waiting),
 *  `watch` (swept, nothing due), `go` (approved/applied), `stop` (rejected/escalated attention). */
export type WorkBadgeKind = 'decide' | 'watch' | 'go' | 'stop';

const BADGE_TONE: Readonly<Record<WorkBadgeKind, Tone>> = {
  decide: 'attention',
  watch: 'neutral',
  go: 'go',
  stop: 'attention',
};

const BADGE_TEXT: Readonly<Record<WorkBadgeKind, string>> = {
  decide: 'text-bracken',
  watch: 'text-text-muted',
  go: 'text-forest',
  stop: 'text-bracken',
};

/** The needs-a-decision / status mini badge (dot + short label; the board card carries NO verdict
 *  control — brief §5.2 — this badge is the whole signal). */
export function WorkMiniBadge({
  kind,
  children,
}: {
  readonly kind: WorkBadgeKind;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <span
      data-badge={kind}
      className={`inline-flex items-center gap-1.5 text-xs font-medium ${BADGE_TEXT[kind]}`}
    >
      <Dot tone={BADGE_TONE[kind]} />
      {children}
    </span>
  );
}

/** A Work section head: the uppercased label plus a muted SOURCE note explaining where the block's
 *  truth comes from (e.g. "the declared item-scoped pulls" — brief §5.3). Composes SectionHead. */
export function WorkSectionHead({
  label,
  source,
}: {
  readonly label: string;
  readonly source?: string;
}): React.JSX.Element {
  return (
    <SectionHead
      label={label}
      right={
        source !== undefined ? (
          <span className="text-xs text-text-subtle">{source}</span>
        ) : undefined
      }
    />
  );
}

/** The frozen-payload content-hash chip (brief §3.3 — the approval binds to exactly this hash). */
export function HashChip({ hash }: { readonly hash: string }): React.JSX.Element {
  return (
    <span
      data-hash={hash}
      className="ritona-numeric inline-flex items-center rounded-sm bg-surface-sunken px-1.5 py-0.5 text-xs text-text-subtle"
    >
      hash {hash}
    </span>
  );
}

/** The evidence as-of pill (brief §3.4 — the honest between-sweeps staleness, never papered over). */
export function AsOfPill({
  asOf,
  stale = false,
}: {
  readonly asOf: string;
  readonly stale?: boolean;
}): React.JSX.Element {
  return (
    <StatusPill tone={stale ? 'attention' : 'neutral'}>
      {stale ? `evidence as of ${asOf} · stale` : `evidence as of ${asOf}`}
    </StatusPill>
  );
}
